import React from 'react';
import { useTranslation } from 'react-i18next';

const formatNum = (n) => {
  if (!n && n !== 0) return '—';
  return Math.round(n)?.toLocaleString('fr-FR')?.replace(/,/g, ' ');
};

const formatUSD = (n) => {
  if (!n && n !== 0) return '$0.00';
  return '$' + (parseFloat(n) || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SkeletonCard = () => (
  <div
    className="rounded-xl p-4 animate-pulse flex-1"
    style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
  >
    <div className="h-3 w-16 rounded mb-3" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-6 w-28 rounded mb-2" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-2 w-20 rounded mb-1" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-2 w-24 rounded" style={{ backgroundColor: '#1f1f1f' }} />
  </div>
);

const ServerComparisonCard = ({ server, serverData, loading, t }) => {
  const color = server?.color || '#888888';
  const serverId = server?.id;

  if (loading) return <SkeletonCard />;

  return (
    <div
      className="rounded-xl p-4 flex-1"
      style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="px-2 py-0.5 rounded text-xs font-bold"
          style={{ backgroundColor: `${color}22`, color, fontFamily: 'Sora, sans-serif' }}
        >
          {serverId}
        </span>
        {server?.name && server?.name !== serverId && (
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
            {server?.name}
          </span>
        )}
      </div>
      <p className="text-xl font-bold mb-1" style={{ color, fontFamily: 'Sora, sans-serif' }}>
        {formatNum(serverData?.totalDiamonds)}
      </p>
      <p className="text-xs mb-0.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
        {t('admin.performance.diamonds', 'diamonds')}
      </p>
      <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
        <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
          {serverData?.activeCreators ?? 0}
        </span>{' '}
        {t('admin.performance.activeCreatorsLabel', 'créateurs actifs')}
      </p>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
        {t('admin.performance.bonusLabel', 'Bonus :')}{' '}
        <span style={{ color: '#22c55e' }}>{formatUSD(serverData?.totalBonus)}</span>
      </p>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
        {t('admin.performance.avgLabel', 'Moy.')} :{' '}
        <span style={{ color }}>{formatNum(Math.round(serverData?.avgDiamonds || 0))}</span>
      </p>
    </div>
  );
};

const ServerComparison = ({ allData, loading, serversData }) => {
  const { t } = useTranslation();

  const getServerData = (serverId) => {
    const rows = (allData || [])?.filter(r => r?.server === serverId);
    if (!rows?.length) return { totalDiamonds: 0, activeCreators: 0, totalBonus: 0, avgDiamonds: 0 };
    const totalDiamonds = rows?.reduce((sum, r) => sum + (parseInt(r?.diamonds) || 0), 0);
    const activeCreators = rows?.filter(r => (parseInt(r?.diamonds) || 0) > 0)?.length;
    const totalBonus = rows?.reduce((sum, r) => sum + (parseFloat(r?.total_bonus) || 0), 0);
    const avgDiamonds = activeCreators > 0 ? totalDiamonds / activeCreators : 0;
    return { totalDiamonds, activeCreators, totalBonus, avgDiamonds };
  };

  if (!serversData?.length && !loading) return null;

  return (
    <div className="flex gap-4 flex-wrap">
      {(serversData || [])?.map(server => (
        <ServerComparisonCard
          key={server?.id}
          server={server}
          serverData={loading ? null : getServerData(server?.id)}
          loading={loading}
          t={t}
        />
      ))}
    </div>
  );
};

export default ServerComparison;
